<script setup>
/**
 * The deal pipeline as a kanban board, cards draggable between its columns.
 *
 * Forked from `SupportCases/CaseBoard.vue` — same drag contract, one board's
 * worth of difference: a column here is always a real stage of the selected
 * pipeline (a deal has exactly one pipeline, so there is no category fallback),
 * and `GET /opportunities` answers a pipeline in one array, so there is no
 * per-column pagination to page through.
 *
 * The store is the source of truth for membership; the local copies exist only
 * because vuedraggable mutates the arrays it renders. A drop emits `move` and
 * nothing else — the screen owns the mutation, the store owns the optimistic
 * update and its rollback, and the props flowing back down resync the copies
 * either way.
 */
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import Button from 'dashboard/components-next/button/Button.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import OpportunityBoardCard from './OpportunityBoardCard.vue';

const props = defineProps({
  // `[{ key, stage, opportunities }]` in board order.
  columns: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  // Truthy when the board fetch failed; the message is this component's to own.
  hasError: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['move', 'retry']);

const SKELETON_CARDS = 3;

// The stage's meaning, whatever the workspace named it. Mirrors the `category`
// enum on `pipeline_stages`; an unknown one (a newer server than this bundle)
// renders neutral rather than blank.
const CATEGORY_DOT_CLASS = {
  open: 'bg-n-blue-9',
  won: 'bg-n-teal-9',
  lost: 'bg-n-ruby-9',
};

const { t } = useI18n();

// vuedraggable mutates `:list` in place, so each column drags a copy of its
// deals rather than the store's own array.
const localColumns = ref([]);

watch(
  () => props.columns,
  columns => {
    localColumns.value = columns.map(column => ({
      ...column,
      opportunities: [...column.opportunities],
    }));
  },
  { immediate: true, deep: true }
);

const dotClass = category => CATEGORY_DOT_CLASS[category] || 'bg-n-slate-9';

// Only the receiving column reports `added` for a cross-column drop; the source
// column's `removed` is the same move seen from the other side, so one handler
// emits exactly one event per drop. A reorder within a column is `moved` and
// means nothing here — a stage has no order of its own.
const onChange = (column, event) => {
  if (!event.added) return;
  emit('move', { opportunity: event.added.element, stage: column.stage });
};
</script>

<template>
  <EmptyStateLayout
    v-if="hasError"
    compact
    icon="i-lucide-triangle-alert"
    :title="t('OPPORTUNITIES.BOARD.ERROR.TITLE')"
    :subtitle="t('OPPORTUNITIES.BOARD.ERROR.SUBTITLE')"
    data-testid="opportunity-board-error"
  >
    <template #actions>
      <Button
        size="sm"
        :label="t('OPPORTUNITIES.BOARD.ERROR.ACTION')"
        @click="emit('retry')"
      />
    </template>
  </EmptyStateLayout>

  <EmptyStateLayout
    v-else-if="!localColumns.length"
    compact
    icon="i-lucide-kanban"
    :title="t('OPPORTUNITIES.BOARD.NO_STAGES.TITLE')"
    :subtitle="t('OPPORTUNITIES.BOARD.NO_STAGES.SUBTITLE')"
    data-testid="opportunity-board-no-stages"
  />

  <div
    v-else
    class="flex items-stretch w-full h-full min-h-0 gap-3 overflow-x-auto"
    data-testid="opportunity-board"
  >
    <section
      v-for="column in localColumns"
      :key="column.key"
      class="flex flex-col w-72 shrink-0 min-h-0 rounded-xl bg-n-alpha-1"
      :aria-label="column.stage.name"
      :data-testid="`opportunity-board-column-${column.key}`"
    >
      <header class="flex items-center gap-2 px-3 py-2.5">
        <span
          class="rounded-full size-2 shrink-0"
          :class="dotClass(column.stage.category)"
          aria-hidden="true"
        />
        <h3 class="mb-0 text-sm font-medium truncate text-n-slate-12">
          {{ column.stage.name }}
        </h3>
        <span class="text-xs tabular-nums text-n-slate-11">
          {{ column.opportunities.length }}
        </span>
      </header>

      <div
        v-if="isLoading && !column.opportunities.length"
        role="status"
        :aria-label="t('OPPORTUNITIES.BOARD.LOADING')"
        aria-busy="true"
        class="flex flex-col gap-2 px-2 pb-2"
      >
        <Skeleton
          v-for="card in SKELETON_CARDS"
          :key="card"
          variant="text"
          :announce="false"
          class="h-20 rounded-lg"
        />
      </div>

      <Draggable
        v-else
        :list="column.opportunities"
        item-key="id"
        group="opportunity-board"
        ghost-class="opacity-50"
        class="flex flex-col min-h-16 gap-2 px-2 pb-2 overflow-y-auto grow"
        @change="onChange(column, $event)"
      >
        <template #item="{ element }">
          <OpportunityBoardCard :opportunity="element" class="cursor-grab" />
        </template>
        <template #footer>
          <p
            v-if="!column.opportunities.length"
            class="px-1 py-2 mb-0 text-xs text-center text-n-slate-10"
          >
            {{ t('OPPORTUNITIES.BOARD.EMPTY_COLUMN') }}
          </p>
        </template>
      </Draggable>
    </section>
  </div>
</template>
